import React, { useEffect, useMemo, useState } from 'react';
import { Field, FieldTypes, LabelWithSubfield, Option, useAutoLoadingData } from '@common-fe/common-fe';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { REQUIRED_TEXT } from '@/common/constants';
import { usePayoutDefinitionPlansQuery, usePayoutDefinitionQuery } from '@/modules/core/queries';
import { FeatureFlags } from '@/modules/featureFlags/featureFlags.constants';
import { useFeatureFlag } from '@/modules/featureFlags/hooks/useFeatureFlag';
import { ProcessingRuleType, TriggerCategory } from '@/modules/HealthPlan/HealthPlan.types.ts';
import { useFieldsWithDefaultValues } from '@/utils';

import AddNewTriggerTitle from '../AddNewTriggerTitle';
import {
  CARD_PURSE_LOCK,
  CARD_PURSE_UNLOCK,
  LOCK_CARD_PURSE_KEY,
  PLAN_IDS_FOR_CARD_PURSE_LOCK_KEY,
} from '../ProcessingRuleModal.constants';
import useGetTriggers from '../queries/useGetTriggers.query';

import useLinkedPlanOptions from './useLinkedPlanOptions';
import { TierField } from './useTiers';

const { AGGREGATE_ONLY, EMBEDDED_ONLY } = ProcessingRuleType;

export const ADD_NEW_AGGREGATE_TRIGGER_KEY = 'ADD_NEW_AGGREGATE_TRIGGER';
export const ADD_NEW_EMBEDDED_TRIGGER_KEY = 'ADD_NEW_EMBEDDED_TRIGGER';
export const AGGREGATE_TRIGGER_KEY = 'aggregateTrigger';
export const EMBEDDED_TRIGGER_KEY = 'embeddedTrigger';

interface Params {
  tiersValues?: TierField[];
  isLastItem?: boolean;
  defaultValues?: Partial<TierField>;
  handleClearValues?: (id: string) => void;
  processingRuleType?: string;
  isHealthPlanProcessingRule?: boolean;
  isTemplateMode?: boolean;
}

export default ({
  defaultValues,
  handleClearValues,
  processingRuleType,
  tiersValues = [],
  isLastItem,
  isHealthPlanProcessingRule,
  isTemplateMode,
}: Params) => {
  const [activePage, setActivePage] = useState<number>(0);
  const [searchPayoutDefinition, setSearchPayoutDefinition] = useState<string>();

  const isHidePurseLockEnabled = useFeatureFlag(FeatureFlags.HIDE_PURSE_LOCK);
  const linkedPlanOptions = useLinkedPlanOptions();

  const {
    fieldOptions: payoutDefinitions,
    isLoading,
    page,
    totalPages,
  } = usePayoutDefinitionQuery(
    undefined,
    activePage,
    searchPayoutDefinition || '',
    defaultValues?.payoutDefinition
  );

  const { isLoading: isPlanLoading, planOptions } = usePayoutDefinitionPlansQuery(
    defaultValues?.payoutDefinition
  );

  const { pageNumber, searchString, formattedOptions, handleGetLoadingData, handleSearchString } =
    useAutoLoadingData({ options: payoutDefinitions, page, totalPages });

  useEffect(() => {
    setActivePage(pageNumber);
  }, [pageNumber]);
  useEffect(() => {
    setSearchPayoutDefinition(searchString);
  }, [searchString]);

  const { fieldOptions: familyTriggers, isLoading: isFamilyLoading } = useGetTriggers(TriggerCategory.FAMILY);

  const { fieldOptions: embeddedTriggers, isLoading: isEmbeddedLoading } = useGetTriggers(
    TriggerCategory.EMBEDDED
  );

  const usedAggregateTriggers = useMemo(
    () =>
      tiersValues
        .filter((t) => t.aggregateTrigger && t.aggregateTrigger !== defaultValues?.aggregateTrigger)
        .map((t) => t.aggregateTrigger),
    [tiersValues, defaultValues?.aggregateTrigger]
  );
  const usedEmbeddedTriggers = useMemo(
    () =>
      tiersValues
        .filter((t) => t.embeddedTrigger && t.embeddedTrigger !== defaultValues?.embeddedTrigger)
        .map((t) => t.embeddedTrigger),
    [tiersValues, defaultValues?.embeddedTrigger]
  );

  const availableFamilyTriggers = useMemo(
    () => familyTriggers.filter((opt) => !usedAggregateTriggers.includes(opt.key as string)),
    [familyTriggers, usedAggregateTriggers]
  );
  const availableEmbeddedTriggers = useMemo(
    () => embeddedTriggers.filter((opt) => !usedEmbeddedTriggers.includes(opt.key as string)),
    [embeddedTriggers, usedEmbeddedTriggers]
  );

  const familyTriggersWithAdd = useMemo(
    () =>
      [
        {
          key: ADD_NEW_AGGREGATE_TRIGGER_KEY,
          primary: true,
          value: 'Add New Trigger',
          node: <AddNewTriggerTitle />,
        },
        {
          divide: true,
          isGroupDivider: true,
          groupKey: 'triggers',
          groupDividerTitle: '',
        },
        ...availableFamilyTriggers,
      ] as Option[],
    [availableFamilyTriggers]
  );

  const embeddedTriggersWithAdd = useMemo(
    () =>
      [
        {
          key: ADD_NEW_EMBEDDED_TRIGGER_KEY,
          primary: true,
          value: 'Add New Trigger',
          node: <AddNewTriggerTitle />,
        },
        {
          divide: true,
          isGroupDivider: true,
          groupKey: 'triggers',
          groupDividerTitle: '',
        },
        ...availableEmbeddedTriggers,
      ] as Option[],
    [availableEmbeddedTriggers]
  );

  const fields = useMemo<Field[]>(() => {
    const currentFields: Field<string>[] = [
      {
        name: 'name',
        type: FieldTypes.Text,
        label: 'Name',
        placeholder: 'Enter name',
        showRequireIcon: true,
        validator: VALIDATORS.REQUIRED_STRING,
        defaultValue: defaultValues?.name || '',
      },
      {
        name: 'payoutDefinition',
        type: FieldTypes.AutocompleteDropdown,
        label: 'Payout definition',
        singleMode: true,
        showRequireIcon: true,
        options: formattedOptions,
        placeholder: 'Select payout definition',
        validator: yup.string().trim().required(REQUIRED_TEXT),
        isLoading,
        notLimitList: true,
        handleGetLoadingData,
        onSearch: handleSearchString,
        completeValue: defaultValues?.payoutDefinition || '',
        resetCompleteValue: () => handleClearValues,
        onClear: () => {
          if (handleClearValues && defaultValues?.payoutDefinition) {
            handleClearValues(defaultValues.payoutDefinition);
          }
        },
      },
      {
        name: AGGREGATE_TRIGGER_KEY,
        type: FieldTypes.Dropdown,
        singleMode: true,
        label: <LabelWithSubfield title="Aggregate trigger" desc="Effects all dependents within the plan" />,
        showRequireIcon: processingRuleType !== EMBEDDED_ONLY,
        options: familyTriggersWithAdd,
        placeholder: 'Select trigger',
        isLoading: isFamilyLoading,
        validator: yup.string().trim().required(REQUIRED_TEXT),
        defaultValue: defaultValues?.aggregateTrigger || '',
        isAbsent: isLastItem || tiersValues.length <= 1 || processingRuleType === EMBEDDED_ONLY,
      },
      {
        name: LOCK_CARD_PURSE_KEY,
        type: FieldTypes.Radio,
        expandItems: true,
        label: (
          <LabelWithSubfield
            title="Lock card purse"
            desc="Secures the purse associated to plan, blocking all transactions."
            style={{ maxWidth: '230px' }}
          />
        ),
        options: [
          { label: 'Lock', value: CARD_PURSE_LOCK },
          { label: 'Unlock', value: CARD_PURSE_UNLOCK },
        ],
        defaultValue: defaultValues?.lockCardPurse || CARD_PURSE_UNLOCK,
        validator: VALIDATORS.STRING,
        isAbsent:
          isHidePurseLockEnabled ||
          !isHealthPlanProcessingRule ||
          !!isTemplateMode ||
          processingRuleType === EMBEDDED_ONLY ||
          isLastItem,
        subFields: [
          {
            parentValue: CARD_PURSE_LOCK,
            name: PLAN_IDS_FOR_CARD_PURSE_LOCK_KEY,
            type: FieldTypes.CheckboxDropdown,
            placeholder: 'Select plans',
            options: linkedPlanOptions || [],
            label: '',
            emptyListMessage: 'No plans have been found',
            validator: VALIDATORS.REQUIRED_ARRAY,
            defaultValue: defaultValues?.planIdsForCardPurseLock || [],
          },
        ] as Field[],
      },
      {
        name: EMBEDDED_TRIGGER_KEY,
        type: FieldTypes.Dropdown,
        singleMode: true,
        optional: processingRuleType !== EMBEDDED_ONLY,
        label: <LabelWithSubfield title="Embedded trigger" desc="Effects each dependent independently" />,
        showRequireIcon: processingRuleType === EMBEDDED_ONLY,
        options: embeddedTriggersWithAdd,
        placeholder: 'Select trigger',
        isLoading: isEmbeddedLoading,
        validator:
          processingRuleType === EMBEDDED_ONLY
            ? yup.string().trim().required(REQUIRED_TEXT)
            : yup.string().trim(),
        defaultValue: defaultValues?.embeddedTrigger || '',
        isAbsent: isLastItem || tiersValues.length <= 1 || processingRuleType === AGGREGATE_ONLY,
      },
      ...(defaultValues?.aggregateTrigger || defaultValues?.embeddedTrigger
        ? ([
            {
              name: 'isSendTriggerNotification',
              type: FieldTypes.Checkbox,
              label: 'Send trigger notification',
              value: defaultValues?.isSendTriggerNotification || false,
            },
          ] as Field[])
        : []),
      {
        name: 'displayName',
        type: FieldTypes.Checkbox,
        label: 'Display name',
        value: defaultValues?.displayName || false,
        subFields: [
          {
            parentValue: true,
            name: 'nameToDisplay',
            type: FieldTypes.Text,
            label: 'Name to display',
            placeholder: 'Enter display name',
            validator: VALIDATORS.REQUIRED_STRING,
            defaultValue: defaultValues?.nameToDisplay || '',
          },
          {
            parentValue: true,
            name: 'planId',
            type: FieldTypes.AutocompleteDropdown,
            label: 'Plan',
            singleMode: true,
            options: planOptions,
            placeholder: 'Select plan',
            validator: yup.string().trim().required(REQUIRED_TEXT),
            isLoading: isPlanLoading,
            notLimitList: true,
            defaultValue: defaultValues?.planId || '',
            value: defaultValues?.planId || '',
            cleaningInitiator: defaultValues?.payoutDefinition,
          },
        ] as Field[],
      },
    ];
    const formatedFields = useFieldsWithDefaultValues(currentFields, defaultValues);
    return formatedFields;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    defaultValues,
    formattedOptions,
    handleGetLoadingData,
    handleSearchString,
    isLoading,
    familyTriggersWithAdd,
    embeddedTriggersWithAdd,
    isFamilyLoading,
    isEmbeddedLoading,
    isPlanLoading,
    planOptions,
    processingRuleType,
    isLastItem,
    isHidePurseLockEnabled,
    linkedPlanOptions,
    isHealthPlanProcessingRule,
    isTemplateMode,
  ]);

  return fields;
};
